export interface ZipRestrictedEntry {
	name: string;
	extension: string;
}

export interface ZipInspectionResult {
	allowed: boolean;
	restrictedEntries: ZipRestrictedEntry[];
}

export class ZipContentValidator {
	static readonly RESTRICTED_INNER_EXTENSIONS: ReadonlySet<string> = new Set<string>([
		'.exe',
		'.bat',
		'.cmd',
		'.com',
		'.scr',
		'.pif',
		'.msi',
		'.msp',
		'.mst',
		'.dll',
		'.sys',
		'.cpl',
		'.jar',
		'.app',
		'.gadget',
		'.js',
		'.jse',
		'.vbs',
		'.vbe',
		'.vb',
		'.ws',
		'.wsf',
		'.wsc',
		'.wsh',
		'.ps1',
		'.ps1xml',
		'.ps2',
		'.ps2xml',
		'.psc1',
		'.psc2',
		'.msh',
		'.msh1',
		'.msh2',
		'.mshxml',
		'.msh1xml',
		'.msh2xml',
		'.scf',
		'.lnk',
		'.inf',
		'.reg',
		'.hta',
		'.sh',
		'.bash',
		'.csh',
		'.ksh',
		'.zsh',
		'.run',
		'.py',
		'.pyc',
		'.pyo',
		'.pyw',
		'.pl',
		'.php',
		'.rb',
		'.dmg',
		'.pkg',
		'.deb',
		'.rpm',
	]);

	private static readonly CENTRAL_FILE_HEADER_SIGNATURE = 0x02014b50;
	private static readonly EOCD_MIN_SIZE = 22;
	private static readonly MAX_COMMENT_SIZE = 0xffff;

	static async inspect(file: File): Promise<ZipInspectionResult> {
		const buffer = await file.arrayBuffer();
		const bytes = new Uint8Array(buffer);
		const view = new DataView(buffer);

		const eocdOffset = ZipContentValidator.findEndOfCentralDirectory(bytes);
		if (eocdOffset < 0) {
			return { allowed: true, restrictedEntries: [] };
		}

		const entryCount = view.getUint16(eocdOffset + 10, true);
		let pointer = view.getUint32(eocdOffset + 16, true);

		if (pointer === 0xffffffff || entryCount === 0xffff) {
			return { allowed: true, restrictedEntries: [] };
		}

		const restrictedEntries: ZipRestrictedEntry[] = [];
		for (let index = 0; index < entryCount; index++) {
			if (pointer + 46 > bytes.length) {
				break;
			}
			if (view.getUint32(pointer, true) !== ZipContentValidator.CENTRAL_FILE_HEADER_SIGNATURE) {
				break;
			}

			const nameLength = view.getUint16(pointer + 28, true);
			const extraLength = view.getUint16(pointer + 30, true);
			const commentLength = view.getUint16(pointer + 32, true);
			const nameStart = pointer + 46;
			const name = ZipContentValidator.decodeName(bytes.subarray(nameStart, nameStart + nameLength));

			const extension = ZipContentValidator.getInnerExtension(name);
			if (extension && ZipContentValidator.RESTRICTED_INNER_EXTENSIONS.has(extension)) {
				restrictedEntries.push({ name, extension });
			}

			pointer = nameStart + nameLength + extraLength + commentLength;
		}

		return { allowed: restrictedEntries.length === 0, restrictedEntries };
	}

	private static findEndOfCentralDirectory(bytes: Uint8Array): number {
		const minSearch = Math.max(
			0,
			bytes.length - (ZipContentValidator.EOCD_MIN_SIZE + ZipContentValidator.MAX_COMMENT_SIZE)
		);
		for (let i = bytes.length - ZipContentValidator.EOCD_MIN_SIZE; i >= minSearch; i--) {
			if (bytes[i] === 0x50 && bytes[i + 1] === 0x4b && bytes[i + 2] === 0x05 && bytes[i + 3] === 0x06) {
				return i;
			}
		}
		return -1;
	}

	private static decodeName(nameBytes: Uint8Array): string {
		return new TextDecoder('utf-8', { fatal: false }).decode(nameBytes);
	}

	private static getInnerExtension(entryName: string): string {
		const normalized = entryName.replace(/\\/g, '/');
		const baseName = normalized.substring(normalized.lastIndexOf('/') + 1);
		const dotIndex = baseName.lastIndexOf('.');
		if (dotIndex <= 0) {
			return '';
		}
		return baseName.substring(dotIndex).toLowerCase();
	}
}
